import React, { useMemo, useState } from "react";
import { THEME, flexxState, awardXP } from "./state";
import { PHASES, getPhase } from "./masteryData";
import { DECKS } from "./flashcardData";
import Portal from "./Portal";
import NextGenCTA from "./NextGenCTA";

// Map phase -> deck id to seed on pass
const PHASE_TO_DECK = {
  phase1: "market",
  phase2: "ta",
  phase3: "risk",
  phase4: "psychology",
};

// Mastery Gate modal — 5 questions, pass threshold gates unlock
export function MasteryGateQuiz({ phaseId, onClose, onPassed }) {
  const phase = useMemo(() => getPhase(phaseId), [phaseId]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [result, setResult] = useState(null);

  if (!phase) return null;
  const q = phase.questions[idx];

  const choose = (i) => {
    if (locked) return;
    setSelected(i);
    setLocked(true);
    const next = { ...answers, [idx]: i };
    setAnswers(next);
    setTimeout(() => {
      if (idx + 1 >= phase.questions.length) {
        const correctCount = phase.questions.reduce(
          (acc, qq, qi) => acc + (next[qi] === qq.correct ? 1 : 0),
          0
        );
        const pct = Math.round((correctCount / phase.questions.length) * 100);
        const passed = pct >= phase.threshold;
        const prev = flexxState.gates()[phaseId];
        const attempts = (prev?.attempts || 0) + 1;
        flexxState.saveGate(phaseId, { passed, score: pct, attempts, lastAt: Date.now() });
        if (passed) {
          const xp = attempts === 1 ? 100 : 50;
          awardXP(xp, `Passed ${phase.name}`);
          if (pct === 100) awardXP(150, "Perfect score");
          // Seed the corresponding flashcard deck so spaced-repetition kicks in
          const deckId = PHASE_TO_DECK[phaseId];
          if (deckId) {
            const deck = DECKS.find((d) => d.id === deckId);
            if (deck) flexxState.seedFlashcardDeck(deck.cards);
          }
          onPassed && onPassed(phaseId);
        }
        setResult({ pct, passed, correctCount, total: phase.questions.length });
      } else {
        setIdx(idx + 1);
        setSelected(null);
        setLocked(false);
      }
    }, 650);
  };

  const retry = () => {
    setIdx(0);
    setAnswers({});
    setSelected(null);
    setLocked(false);
    setResult(null);
  };

  if (result) {
    return (
      <Portal>
      <div data-testid="mastery-gate-result" style={overlay}>
        <div className="flexx-slide-up" style={container}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>MASTERY GATE RESULT</div>
          <h2 style={{ color: THEME.text, fontSize: 22, margin: "6px 0 8px" }}>{phase.name}</h2>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 42, fontWeight: 800, color: result.passed ? THEME.green : THEME.red }}>
              {result.pct}%
            </div>
            <div style={{ color: THEME.mute, fontSize: 12 }}>
              {result.correctCount} / {result.total} correct · Gate {phase.threshold}%
            </div>
          </div>

          {result.passed ? (
            <>
              <div className="flexx-card" style={{ borderColor: `${THEME.green}70`, background: `${THEME.green}15` }}>
                <div style={{ color: THEME.green, fontWeight: 700, marginBottom: 4 }}>✓ Passed — next phase unlocked</div>
                <div style={{ color: THEME.text, fontSize: 13, lineHeight: 1.5 }}>
                  Solid work. You just proved you understand this phase well enough to apply it.
                  That's the FLEXX standard — we don't move on until it's locked in.
                </div>
              </div>
              {phaseId === "phase4" && (
                <NextGenCTA variant="celebration" studentName={flexxState.profile()?.name || ""} />
              )}
            </>
          ) : (
            <div className="flexx-card" style={{ borderColor: `${THEME.red}70`, background: `${THEME.red}15` }}>
              <div style={{ color: THEME.red, fontWeight: 700, marginBottom: 4 }}>Gate not yet cleared</div>
              <div style={{ color: THEME.text, fontSize: 13, lineHeight: 1.55 }}>
                You scored {result.pct}% — below the {phase.threshold}% gate. Don't sweat it.
                Re-read the module, then take a different shuffle of questions. You've got this.
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            {!result.passed && (
              <button data-testid="mastery-gate-retry-btn" className="flexx-btn-primary" style={{ flex: 1 }} onClick={retry}>
                Retake Gate
              </button>
            )}
            <button data-testid="mastery-gate-close-btn" className="flexx-btn-ghost" style={{ flex: 1 }} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
      </Portal>
    );
  }

  const progressPct = Math.round(((idx + (locked ? 1 : 0)) / phase.questions.length) * 100);
  return (
    <Portal>
    <div data-testid="mastery-gate-modal" style={overlay}>
      <div className="flexx-slide-up" style={container}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>MASTERY GATE · {phase.threshold}% TO PASS</div>
            <h3 style={{ color: THEME.text, margin: "6px 0 0", fontSize: 18 }}>{phase.name}</h3>
          </div>
          <button aria-label="Close" onClick={onClose} data-testid="mastery-gate-x" style={{ background: "transparent", border: "none", color: THEME.mute, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>

        <div className="flexx-progress-bar" style={{ margin: "14px 0 18px" }}>
          <span style={{ width: `${progressPct}%` }} />
        </div>

        <div style={{ color: THEME.mute, fontSize: 11, marginBottom: 8 }}>Question {idx + 1} of {phase.questions.length}</div>
        <p style={{ color: THEME.text, fontSize: 15, lineHeight: 1.55, marginBottom: 14 }}>{q.q}</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {q.choices.map((c, i) => {
            const isSel = selected === i;
            const showCorrect = locked && i === q.correct;
            const showWrong = locked && isSel && i !== q.correct;
            const cls = ["flexx-quiz-option", isSel && !locked ? "selected" : "", showCorrect ? "correct" : "", showWrong ? "wrong flexx-shake" : ""].join(" ");
            return (
              <button
                key={i}
                data-testid={`mastery-gate-option-${idx}-${i}`}
                disabled={locked}
                className={cls}
                onClick={() => choose(i)}
              >
                <span style={{ width: 22, height: 22, borderRadius: "50%", border: `1px solid ${THEME.border}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: THEME.mute }}>
                  {String.fromCharCode(65 + i)}
                </span>
                <span>{c}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
    </Portal>
  );
}

// Mastery Gates Hub — list of all phases with lock/in-progress/passed states
export function MasteryGatesHub() {
  const [openPhase, setOpenPhase] = useState(null);
  const [gatesState, setGatesState] = useState(flexxState.gates());

  const refresh = () => setGatesState(flexxState.gates());

  const isUnlocked = (i) => {
    if (i === 0) return true;
    const prev = PHASES[i - 1];
    return gatesState[prev.id]?.passed;
  };

  return (
    <div className="flexx-card" data-testid="mastery-gates-hub">
      <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 6 }}>MASTERY GATES</div>
      <div style={{ color: THEME.text, fontWeight: 700, marginBottom: 14 }}>12-Week Mastery Protocol</div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {PHASES.map((p, i) => {
          const unlocked = isUnlocked(i);
          const state = gatesState[p.id];
          const passed = state?.passed;
          const color = passed ? THEME.gold : unlocked ? THEME.teal : THEME.border;
          const icon = passed ? "✓" : unlocked ? "●" : "🔒";

          return (
            <div
              key={p.id}
              data-testid={`mastery-gate-item-${p.id}`}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: 12, borderRadius: 10,
                border: `1px solid ${passed ? THEME.gold + "70" : unlocked ? THEME.teal + "70" : THEME.border}`,
                background: passed ? THEME.goldSoft : unlocked ? THEME.tealSoft : "transparent",
                opacity: unlocked ? 1 : 0.55,
              }}
            >
              <div
                style={{
                  width: 34, height: 34, borderRadius: "50%",
                  border: `2px solid ${color}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: color, fontWeight: 800, fontSize: 14,
                }}
                className={unlocked && !passed ? "flexx-pulse-ring" : ""}
              >
                {icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: THEME.text, fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                <div style={{ color: THEME.mute, fontSize: 11, marginTop: 2 }}>
                  Gate: {p.threshold}% {state ? `· Last score: ${state.score}% · Attempts: ${state.attempts}` : "· Not attempted"}
                </div>
              </div>
              <button
                disabled={!unlocked}
                data-testid={`mastery-gate-start-${p.id}`}
                className={passed ? "flexx-btn-ghost" : "flexx-btn-primary"}
                style={{ fontSize: 12, padding: "8px 14px" }}
                onClick={() => setOpenPhase(p.id)}
              >
                {passed ? "Retake" : unlocked ? "Take Gate" : "Locked"}
              </button>
            </div>
          );
        })}
      </div>

      {openPhase && (
        <MasteryGateQuiz
          phaseId={openPhase}
          onClose={() => { setOpenPhase(null); refresh(); }}
          onPassed={() => refresh()}
        />
      )}
    </div>
  );
}

const overlay = {
  position: "fixed", inset: 0, zIndex: 9800,
  background: "rgba(0,0,0,0.88)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};
const container = {
  background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 18, padding: 28,
  maxWidth: 560, width: "100%", maxHeight: "92vh", overflowY: "auto",
};
